/**
   * @ngdoc directive
   * @name adjust.directive:rightSidebar
   * @scope
   * @restrict E
   *
   * @description
   * Right sidebar container
   *
   * @param {string} name sidebar title
   * @param {string} withClose show close button
   * @param {boolean=true} backdrop if backdrop needs to be present
   * @param {Function} onClose close callback
   *
   */
function rightSidebar() {
  'ngInject';
  return {
    restrict: 'E',
    template: require('./right-sidebar.html'),
    scope: {},
    controller: 'RightSidebarController',
    controllerAs: 'vm',
    bindToController: {
      sidebarId: '@',
      withClose: '@',
      backdrop: '@',
      onClose: '&',
      extraClasses: '@'
    },
    transclude: true
  };
}

/**
   * @ngdoc controller
   * @name adjust.controller:RightSidebarController
   *
   * @description
   * All the functionality of the {@link adjust.directive:rightSidebar rightSidebar} directive
   *
   */
function RightSidebarController($scope, RightSidebar, $document, $state, $timeout) {
  'ngInject';
  var vm = this;

  var _highLightCB;

  vm.open = false;
  vm.sidebarBackdrop = angular.isDefined(vm.backdrop) ? $scope.$eval(vm.backdrop) : true;

  vm.onDismiss = onDismiss;
  vm.close = close;

  $scope.$on('rightSidebar:init', _handleInit);
  $scope.$on('rightSidebar:toggle', _handleToggle);
  $scope.$on('rightSidebar:open', _handleOpen);
  $scope.$on('rightSidebar:close', _handleClose);
  $scope.$on('rightSidebar:theme', _handleThemeChange);
  $scope.$on('rightSidebar:size', _handleSizeChange);
  $scope.$on('rightSidebar:dismiss', _handleDismissChange);
  $scope.$on('rightSidebar:reset', _handleReset);
  $scope.$on('rightSidebar:setBreadcrumbs', _handleSetBreadcrumbs);
  $scope.$on('rightSidebar:resetBreadcrumbs', _handleResetBreadcrumb);
  $scope.$on('highlight:init', _handleHighLight);
  $scope.$on('$destroy', _handleDestroy);

  _readSettings();

  /**
   * @private
   */
  function _readSettings() {
    vm.sidebarTheme = RightSidebar.getTheme();
    vm.sidebarSize = RightSidebar.getSize();
    vm.sidebarDismissible = RightSidebar.isDismissible();
  }

  /**
   * @ngdoc method
   * @name _handleInit
   * @methodOf adjust.controller:RightSidebarController
   *
   * @description
   * Handle right sidebar init
   *
   * @param {Object} e event object
   * @param {Object} params params to set
   * @private
   */
  function _handleInit(e, params) {

    RightSidebar.init(params);

    vm.sidebarTheme = RightSidebar.getTheme();
    vm.sidebarSize = RightSidebar.getSize();
    vm.sidebarDismissible = RightSidebar.isDismissible();

  }

  /**
   * @ngdoc method
   * @name _handleHighLight
   * @methodOf adjust.controller:RightSidebarController
   *
   * @description
   * Broadcast highlight event down to the highlight directive
   *
   * @private
   */
  function _handleHighLight() {

    $scope.$broadcast('highlight:on', true);

  }

  /**
   * @ngdoc method
   * @name _bindCloseEvents
   * @methodOf adjust.controller:RightSidebarController
   *
   * @description
   * Bind events for sidebar close handle: **keyup** on $document
   *
   * @private
   */
  function _bindCloseEvents() {

    if (!RightSidebar.isDismissible()) { return; }

    $document.bind('keyup', _handleKeyClose);

  }

  /**
   * @ngdoc method
   * @name _unbindCloseEvents
   * @methodOf adjust.controller:RightSidebarController
   *
   * @description
   * Unbind events for sidebar close handle: **keyup** on $document
   *
   * @private
   */
  function _unbindCloseEvents() {

    if (!RightSidebar.isDismissible()) { return; }

    $document.unbind('keyup', _handleKeyClose);

  }

  /**
   * @param {Event} event
   * @param {Object} data
   * @param {String} data.sidebarId
   * @private
   */
  function _handleToggle(event, data = {}) {
    if (data.sidebarId && data.sidebarId !== vm.sidebarId) {
      return;
    }

    vm.open = !vm.open;

    if (vm.open) {
      _bindCloseEvents();
    } else {
      RightSidebar.reset();
      _unbindCloseEvents();
    }

  }

  /**
   * @ngdoc method
   * @name _handleClose
   * @methodOf adjust.controller:RightSidebarController
   *
   * @description
   * Handle right sidebar close
   *
   * @private
   */
  function _handleClose() {

    vm.open = false;

    RightSidebar.reset();

    _handleResetBreadcrumb();
    _unbindCloseEvents();

  }

  /**
   * @ngdoc method
   * @name _handleOpen
   * @methodOf adjust.controller:RightSidebarController
   *
   * @description
   * Handle right sidebar toggle event
   *
   * @private
   */
  function _handleOpen() {

    vm.open = true;
    _bindCloseEvents();
  }

  /**
   * @ngdoc method
   * @name _handleThemeChange
   * @methodOf adjust.controller:RightSidebarController
   *
   * @description
   * Handle right sidebar theme change
   *
   * @param {Object} e event object
   * @param {string} theme theme of the right sidebar
   * @private
   */
  function _handleThemeChange(e, theme) {

    vm.sidebarTheme = theme;

    RightSidebar.setTheme(theme);

  }

  /**
   * @ngdoc method
   * @name _handleSizeChange
   * @methodOf adjust.controller:RightSidebarController
   *
   * @description
   * Handle right sidebar size change
   *
   * @param {Object} e event object
   * @param {string} size size of the right sidebar
   * @private
   */
  function _handleSizeChange(e, size) {

    vm.sidebarSize = size;

    RightSidebar.setSize(size);

  }

  /**
   * @ngdoc method
   * @name _handleDismissChange
   * @methodOf adjust.controller:RightSidebarController
   *
   * @description
   * Handle right sidebar dismissible flag change
   *
   * @param {Object} e event object
   * @param {boolean} dismiss dismiss sidebar flag
   * @private
   */
  function _handleDismissChange(e, dismiss) {

    vm.sidebarDismissible = dismiss;

    RightSidebar.setDismissible(dismiss);

  }

  /**
   * Handles resets triggered from the breadcrumbs
   *
   * @private
   */
  function _handleReset() {
    $timeout(() => {
      RightSidebar.reset();
      _readSettings();
    });
  }

  /**
   * @ngdoc method
   * @name _handleSetBreadcrumbs
   * @methodOf adjust.controller:RightSidebarController
   *
   * @description
   * Handles setting the right sidebar breadcrumbs
   *
   * @param {Object} e event object
   * @param {Array} steps
   * @private
   */
  function _handleSetBreadcrumbs(e, steps) {

    $timeout(function() {
      $scope.$broadcast('rightSidebarBreadcrumbs:set', angular.copy(steps));
    });

  }

  /**
   * @ngdoc method
   * @name _handleResetBreadcrumb
   * @methodOf adjust.controller:RightSidebarController
   *
   * @description
   * Handles resetting the right sidebar breadcrumbs
   *
   * @private
   */
  function _handleResetBreadcrumb() {

    $timeout(function() {
      $scope.$broadcast('rightSidebarBreadcrumbs:set', []);
    });

  }

  /**
   * @ngdoc method
   * @name _handleKeyClose
   * @methodOf adjust.controller:RightSidebarController
   *
   * @description
   * Handles sidebar close when ESC is pressed
   *
   * @private
   */
  function _handleKeyClose(e) {

    if (e.keyCode !== 27) {
      return false;
    }

    close();
    $scope.$digest();

  }

  /**
   * @ngdoc method
   * @name _handleDestroy
   * @methodOf adjust.controller:RightSidebarController
   *
   * @description
   * Handle right sidebar destroy
   *
   * @private
   */
  function _handleDestroy() {

    RightSidebar.reset();

    _unbindCloseEvents();

  }

  /**
   * @ngdoc method
   * @name onDismiss
   * @methodOf adjust.controller:RightSidebarController
   *
   * @description
   *
   * Register on dismiss callback for the child highlight directive
   * @param {Function} c callback to fire when trying to dismiss
   */
  function onDismiss(c) {

    _highLightCB = c;

  }

  /**
   * @ngdoc method
   * @name close
   * @methodOf adjust.controller:RightSidebarController
   *
   * @description
   * Close the right sidebar
   */
  function close() {

    if (!RightSidebar.isDismissible() || vm.sidebarTheme === 'dimmed') {

      if (angular.isFunction(_highLightCB)) {
        _highLightCB.call();
      }

      return;
    }

    _handleClose();

    if (angular.isFunction(vm.onClose)) {
      vm.onClose();
    }

  }
}

export { rightSidebar, RightSidebarController };
